import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { MultiSelectChangeEvent } from 'primeng/multiselect';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { FilterService } from 'src/app/services/filter.service';
import { UserPreferenceType } from 'src/app/shared/app.enums';
import { AppUtils } from 'src/app/shared/app.utils';
import { GeographicFilterModel } from 'src/app/shared/models/geographic-filter-model';

@Component({
  selector: 'ness-filter-preferences',
  templateUrl: './filter-preferences.component.html'
})
export class FilterPreferencesComponent implements OnInit, OnDestroy {
  // Subject observables to debounce multiselect dropdown components
  selectedFDGroupsSubj$: Subject<ServerModels.FieldDivisionGroupViewModel[]> = new Subject<ServerModels.FieldDivisionGroupViewModel[]>();
  selectedCountiesSubj$: Subject<ServerModels.ENESS_REF_Counties[]> = new Subject<ServerModels.ENESS_REF_Counties[]>();
  selectedAgenciesSubj$: Subject<ServerModels.ENESS_REF_Agency[]> = new Subject<ServerModels.ENESS_REF_Agency[]>();

  filterModel: GeographicFilterModel = new GeographicFilterModel('all', 'all', [], 'all', 'all', [], 'all', [], 'All', 'All', 'All', 'All', 'All', 'All', 'All', 'All');
  isLoading: boolean = false;

  private _destroyed$ = new Subject<void>();

  constructor(public filterService: FilterService,
    public elementRef: ElementRef
  ) {

    this.filterService.filterPreferences.subscribe((fm: GeographicFilterModel) => {
      this.filterModel = fm;
    });
  }

  ngOnInit(): void {
    this.filterService.initGeoFilters();
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
    this.selectedFDGroupsSubj$.next(null);
    this.selectedFDGroupsSubj$.complete();
    this.selectedCountiesSubj$.next(null);
    this.selectedCountiesSubj$.complete();
  }

  public onSelectFieldDivision(tgt: EventTarget): void {
    const fieldDivision = tgt as HTMLSelectElement;
    this.filterModel = new GeographicFilterModel(fieldDivision.value, 'all', [], 'all', 'all', [], 'all', [], fieldDivision.options[fieldDivision.selectedIndex].text, 'All', 'All', 'All', 'All', 'All', 'All', 'All');
    this.filterService.getFieldDivisionGroups(fieldDivision.value);
    this.filterService.getStates(fieldDivision.value, 'all');
    this.filterService.getCounties(fieldDivision.value, 'all', 'all');
    this.filterService.getAgencies(fieldDivision.value, 'all', 'all', 'all');
    this.filterService.geographicFilterModel$.next(this.filterModel);
  }

  public onSelectFieldDivisionGroup(evt: MultiSelectChangeEvent): void {
    this.isLoading = true;
    if (!this.selectedFDGroupsSubj$.observed) {
      this.selectedFDGroupsSubj$.pipe(debounceTime(2000), takeUntil(this._destroyed$)).subscribe({
        next: (groups: ServerModels.FieldDivisionGroupViewModel[]) => {

          let groupIds: string = groups.map(x => x.IntelligenceReportATFReferralGroupsID)?.join(';');
          if (groups?.length > 0 && groups?.length != this.filterService.fieldDivisionGroups$.getValue()?.length) {
            this.filterModel.groupNameList = '';
            groups.forEach(group => this.filterModel.groupNameList += `${group.GroupName} (${group.Inactive ? 'Inactive' : 'Active'}) `);
          }
          else {
            this.filterModel.groupNameList = 'All';
            groupIds = 'all';
          }

          this.filterModel = new GeographicFilterModel(this.filterModel.fieldDivisionID, groupIds, this.filterModel.fieldDivisionGroups, 'all', 'all', [], 'all', [], this.filterModel.fieldDivisionName, this.filterModel.groupName, this.filterModel.groupNameList, 'All', 'All', 'All', 'All', 'All');
          this.filterService.getStates(this.filterModel.fieldDivisionID, groupIds);
          this.filterService.getCounties(this.filterModel.fieldDivisionID, groupIds, 'all')
          this.filterService.getAgencies(this.filterModel.fieldDivisionID, groupIds, 'all', 'all');
          this.filterService.geographicFilterModel$.next(this.filterModel);
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
        }
      });
    }

    this.selectedFDGroupsSubj$.next(evt.value);
  }

  public onSelectState(tgt: EventTarget): void {
    const state = <HTMLSelectElement>tgt;
    this.filterModel = new GeographicFilterModel(this.filterModel.fieldDivisionID, this.filterModel.fieldDivisionGroupID, this.filterModel.fieldDivisionGroups, state.value, 'all', [], 'all', [], this.filterModel.fieldDivisionName, this.filterModel.groupName, this.filterModel.groupNameList, state.options[state.selectedIndex].text, 'All', 'All', 'All', 'All');
    const groupIds: string = this.filterModel.fieldDivisionGroups?.map(x => x.IntelligenceReportATFReferralGroupsID)?.join(';');
    this.filterService.getCounties(this.filterModel.fieldDivisionID, groupIds, state.value);
    this.filterService.getAgencies(this.filterModel.fieldDivisionID, groupIds, state.value, 'all');
    this.filterService.geographicFilterModel$.next(this.filterModel);
    this.isLoading = false;
  }

  public onSelectCounties(evt: MultiSelectChangeEvent): void {
    this.isLoading = true;
    if (!this.selectedCountiesSubj$.observed) {
      this.selectedCountiesSubj$.pipe(debounceTime(2000), takeUntil(this._destroyed$)).subscribe({
        next: (counties: ServerModels.ENESS_REF_Counties[]) => {

          let groupIds: string = this.filterModel.fieldDivisionGroups?.map(x => x.IntelligenceReportATFReferralGroupsID)?.join(';');
          let countyIds: string = counties.map(x => x.CountyID)?.join(';');

          if (counties?.length > 0 && counties.length != this.filterService.counties$.getValue()?.length) {
            this.filterModel.countyNameList = '';
            counties.forEach(county => this.filterModel.countyNameList += `${county.CountyName}, ${county.ENESS_REF_State?.StateAbbreviation}; `);
          }
          else {
            this.filterModel.countyNameList = 'All';
            countyIds = 'all';
          }

          this.filterModel = new GeographicFilterModel(this.filterModel.fieldDivisionID, groupIds, this.filterModel.fieldDivisionGroups, this.filterModel.stateID, countyIds, counties, 'all', [], this.filterModel.fieldDivisionName, this.filterModel.groupName, this.filterModel.groupNameList, this.filterModel.stateName, this.filterModel.countyName, this.filterModel.countyNameList, 'All', 'All');
          this.filterService.getAgencies(this.filterModel.fieldDivisionID, groupIds, this.filterModel.stateID, countyIds);
          this.filterService.geographicFilterModel$.next(this.filterModel);
          this.isLoading = false;
        },
        error: (err) => this.isLoading = false
      });
    }

    this.selectedCountiesSubj$.next(evt.value);
  }

  public onSelectAgencies(evt: MultiSelectChangeEvent): void {
    this.isLoading = true;
    if (!this.selectedAgenciesSubj$.observed) {
      this.selectedAgenciesSubj$.pipe(debounceTime(1000), takeUntil(this._destroyed$)).subscribe({
        next: (agencies: ServerModels.ENESS_REF_Agency[]) => {
          let agencyIds: string = agencies.map(x => x.AgencyID)?.join(';');

          if (agencies?.length > 0 && agencies?.length != this.filterService.agencies$.getValue()?.length) {
            this.filterModel.agencyNamesList = '';
            agencies.forEach(agency => this.filterModel.agencyNamesList += `${agency.AgencyName}; `);
          } else {
            this.filterModel.agencyNamesList = 'All';
            agencyIds = 'all';
          }

          this.filterModel = new GeographicFilterModel(this.filterModel.fieldDivisionID, this.filterModel.fieldDivisionGroupID, this.filterModel.fieldDivisionGroups, this.filterModel.stateID, this.filterModel.countyID, this.filterModel.counties, agencyIds, agencies, this.filterModel.fieldDivisionName, this.filterModel.groupName, this.filterModel.groupNameList, this.filterModel.stateName, this.filterModel.countyName, this.filterModel.countyNameList, this.filterModel.agencyName, this.filterModel.agencyNamesList);

          this.filterService.geographicFilterModel$.next(this.filterModel);
          this.isLoading = false;
        },
        error: (err) => this.isLoading = false
      });
    }

    this.selectedAgenciesSubj$.next(evt.value);
  }

  public resetFilters(): void {
    let fm = {
      _fieldDivisionID: AppUtils.userFieldDivisionID(),
      _fieldDivisionGroupID: 'all',
      _fieldDivisionGroups: [] as ServerModels.FieldDivisionGroupViewModel[],
      _stateID: 'all',
      _countyID: 'all',
      _counties: [] as ServerModels.ENESS_REF_Counties[],
      _agencyID: 'all',
      _agencies: [] as ServerModels.ENESS_REF_Agency[],
      _fieldDivisionName: 'All',
      _groupName: 'All',
      _groupNameList: 'All',
      _stateName: 'All',
      _countyName: 'All',
      _countyNameList: 'All',
      _agencyName: 'All',
      _agencyNamesList: 'All'
    };

    this.filterService.getUserInfo().UserPreferences.forEach(x => {
      if (x.ENESS_REF_UserPreferenceTypes.UserPreferenceTypeName === UserPreferenceType.DefaultFilterPreferences) {
        fm = JSON.parse(x.UserPreferenceSettingsJson);
      }
    });

    this.filterModel = new GeographicFilterModel(fm['_fieldDivisionID'], fm['_fieldDivisionGroupID'], fm['_fieldDivisionGroups'], fm['_stateID'], fm['_countyID'], fm['_counties'], fm['_agencyID'], fm['_agencies'], fm['_fieldDivisionName'], fm['_groupName'], fm['_groupNameList'], fm['_stateName'], fm['_countyName'], fm['_countyNameList'], fm['_agencyName'], fm['_agencyNamesList']);
    this.filterService.resetGeoFilterDefaults();
    this.filterService.filterPreferences$.next(this.filterModel);
  }

  public saveFilterPreferences(): void {
    let fieldDivisionName: string = this.filterModel.fieldDivisionID.toLowerCase() === 'all' ? 'All' : this.filterModel.fieldDivisionName;
    let groupName = this.filterModel.fieldDivisionGroupID.toLowerCase() === 'all' ? 'All' : this.filterService.fieldDivisionGroups$.getValue().find((x: ServerModels.FieldDivisionGroupViewModel) => x.IntelligenceReportATFReferralGroupsID === this.filterModel.fieldDivisionGroupID)?.GroupName;
    let groupNameList = '';
    this.filterModel.fieldDivisionGroups.forEach(grp => groupNameList += `${grp.GroupName} (${grp.Inactive ? 'Inactive' : 'Active'}); `);
    let stateName = this.filterModel.stateID.toLowerCase() === 'all' ? 'All' : this.filterService.states$.getValue().find((x: ServerModels.ENESS_REF_State) => x.StateID === this.filterModel.stateID)?.StateName;
    let countyName = this.filterModel.countyID.toLowerCase() === 'all' ? 'All' : this.filterService.counties$.getValue().find((x: ServerModels.ENESS_REF_Counties) => x.CountyID === this.filterModel.countyID)?.CountyName;
    let countyNameList = '';
    this.filterModel.counties.forEach(county => countyNameList += `${county.CountyName}, ${county.ENESS_REF_State.StateAbbreviation}; `);
    let agencyName = this.filterModel.agencyID.toLowerCase() === 'all' ? 'All' : this.filterService.agencies$.getValue().find((x: ServerModels.ENESS_REF_Agency) => x.AgencyID === this.filterModel.agencyID)?.AgencyName;
    let agencyNamesList = '';
    this.filterModel.agencies.forEach(agency => agencyNamesList += `${agency.AgencyName}; `);
    let fm: GeographicFilterModel = new GeographicFilterModel(this.filterModel.fieldDivisionID, this.filterModel.fieldDivisionGroupID, this.filterModel.fieldDivisionGroups, this.filterModel.stateID, this.filterModel.countyID, this.filterModel.counties, this.filterModel.agencyID, this.filterModel.agencies, fieldDivisionName, groupName, groupNameList, stateName, countyName, countyNameList, agencyName, agencyNamesList);
    this.filterService.filterPreferences$.next(fm);
    this.filterService.saveFilterPreferences(fm);
  }

}
